import EDTypography from '../../../../../../../../../../../../BaseComponents/Typography/EDTypography';
import DraggableChipList, {
  IItem,
} from '../../../../../../../../../../../../BaseComponents/Chip/DraggableChip/EDDraggableChipList';
import EDIcon from '../../../../../../../../../../../../BaseComponents/Icon/EDIcon';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { IColumn } from '../../../../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import classNames from 'classnames';
import { IColumnTemplate } from '../../../Queries/AvailableColumnTemplatesHandler';
import { IEkonomiskAnalysDates } from '../../../../../../AnalysSettingsHandler/AnalysSettingsHandler';
import EDTooltip from '../../../../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import {
  addPrevYearCols,
  setVandTeckenIfResultColumn,
  getColumnTemplateName,
} from '../../Utils/EkonomiskAnalysColumnSelect.utils';
import Logger from '../../../../../../../../../../../../Utils/Logger';
import EDIconButton from '../../../../../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDButton from '../../../../../../../../../../../../BaseComponents/Buttons/Button/EDButton';
import { TECKENVAND_KOLUMN } from '../../../../../../../Const/EkonomiskAnalysConsts';
import EDCheckbox from '../../../../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { clone } from 'ramda';
import FunctionMenu from '../../../../../../../../../../_Common/Components/FunctionMenu/FunctionMenu/FunctionMenu';
import FunctionItem from '../../../../../../../../../../_Common/Components/FunctionMenu/FunctionItem/FunctionItem';
import EkonomiskAnalysColumnSelectTemplateActions from '../EkonomiskAnalysColumnSelectTemplateActions/EkonomiskAnalysColumnSelectTemplateActions';
import EDAvatar from '../../../../../../../../../../../../BaseComponents/Avatar/EDAvatar';
import { EDAutoCompleteEdit } from '../../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { ISelectChoice } from '../../../../../../../../../../../../BaseComponents/Selects/SelectCommons';
import { DateInputType } from '../../../../../../../../../../../../Utils/dateUtils';

interface IProps {
  selectedColumns: IColumn[];
  selectedTemplate: string;
  availableColumnTemplates: IColumnTemplate[];
  selectedTemplateColumns: IColumn[];
  hasCodePrivilege: boolean;
  changeTemplate: (template: ISelectChoice<string> | null) => void;
  selectedColumnsUpdate: (items: IItem[]) => void;
  selectedTemplateColumnsUpdate: (items: IItem[]) => void;
  setSelectedColumns: (columns: IColumn[]) => void;
  setSelectedTemplateColumns: (columns: IColumn[]) => void;
  addTemplateColumns: () => void;
  addTemplateColumn: (index: number) => void;
  selectedColumnClick: (column?: IColumn) => void;
  dates: IEkonomiskAnalysDates;
  dateInputType: DateInputType;
  selectionDisabled?: boolean;
  selectionDisabledReason?: string;
  isInterval: boolean;
  prevYearCols: boolean;
  setPrevYearCols: (newValue: boolean) => void;
  flipColumnSign: boolean;
  setFlipColumnSign: (newValue: boolean) => void;
  changeColumn: (column: IColumn) => void;

  templateActionsOpen: boolean;
  onSelectTemplateActionsClick: () => void;
  classes?: any;
}

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
  },
  periodPickerContainer: {
    background: theme.palette.grey['300'],
    padding: theme.spacing(2, 4, 0),
  },
  periodPickerEditMode: {
    flex: '0 1 auto',
  },
  periodPicker: {
    minHeight: '10px',
  },
  periodPickerText: {
    fontSize: '1rem',
  },
  flexContainer: {
    flex: '1 1 auto',
    display: 'flex',
    overflowY: 'hidden',
  },
  columnFlexContainer: {
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'column',
  },
  contentContainer: {
    boxSizing: 'border-box',
    flex: '0 1 50%',
    padding: `${theme.spacing(3)} ${theme.spacing(3)} 0`,
    flexDirection: 'column',
    overflowX: 'hidden',
  },
  chipListContainer: {
    overflowY: 'auto',
    flex: '1 1 50%',
  },
  rightContainer: {
    background: theme.palette.grey['200'],
  },
  templateContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    flex: '0 1 auto',
    alignItems: 'center',
  },
  templSelectContainer: { width: '100%', maxWidth: 238 },
  contentButton: {
    padding: 0,
    justifyContent: 'left',
    marginTop: theme.spacing(1),
    fontSize: '1.05rem',
    height: '24px',
  },
  grouped: {
    boxShadow: `0px 8px 4px -2px ${theme.palette.grey[500]}`,
  },
  inlineToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  moveColumnAvatar: {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.light,
  },
  moveColumnAvatarIcon: {
    color: theme.palette.common.white,
  },
  moveColumnAvatarChildren: {
    height: '24px',
    width: '24px',
  },
});

const EkonomiskAnalysColumnSelectContent = (props: IProps) => {
  const { classes } = props;

  const clearAllSelectedColumns = () => {
    props.selectedColumnsUpdate([]);
  };

  const onPrevYearColsSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    props.setPrevYearCols(checked);

    let newSelectedTemplateColumns = props.selectedTemplateColumns;

    if (checked) {
      newSelectedTemplateColumns = addPrevYearCols(
        props.selectedTemplateColumns
      );
    } else if (props.selectedTemplate) {
      // Reset selected template columns to the original selected templates columns (Remove prev year columns)
      let template = props.availableColumnTemplates.find(
        x => x.id === props.selectedTemplate
      );

      if (template) {
        if (props.flipColumnSign) {
          newSelectedTemplateColumns = template.columns.map(
            setVandTeckenIfResultColumn(true)
          );
        } else {
          newSelectedTemplateColumns = template.columns;
        }
      } else {
        Logger.error('Could not reset to selected template columns');
      }
    }

    props.setSelectedTemplateColumns(newSelectedTemplateColumns);
  };

  const onFlipSignSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    props.setFlipColumnSign(checked);

    const newSelectedTemplateColumns = props.selectedTemplateColumns.map(
      setVandTeckenIfResultColumn(checked)
    );

    props.setSelectedTemplateColumns(newSelectedTemplateColumns);
  };

  const createOnHiddenClick = (item: IItem) => (checked: boolean) => {
    const col = clone(
      props.selectedColumns.find(column => column.id === item.id)
    );

    if (col) {
      col.data.HIDE_COLUMN = !checked;
      props.changeColumn(col);
    }
  };

  const createOnEditClick = (item: IItem) => () => {
    const column = item
      ? props.selectedColumns.find(x => x.id === item.id)
      : undefined;
    props.selectedColumnClick(column);
  };

  const createOnDeleteClick = (item: IItem) => () => {
    const tempColumns = clone(props.selectedColumns).filter(
      column => column.id !== item.id
    );
    props.selectedColumnsUpdate(tempColumns);
  };

  const kolumnMallarOptions = props.availableColumnTemplates
    .filter(x => !props.prevYearCols || x.canHavePrevYearCols)
    .map(template => ({
      header: getColumnTemplateName(
        template.name,
        props.prevYearCols,
        props.flipColumnSign
      ),
      value: template.id,
    }));

  return (
    <div className={classes.container}>
      <div className={classes.flexContainer}>
        <div className={classes.contentContainer}>
          <div className={classes.inlineToolbar}>
            <EDTypography variant="h6" color="textPrimary" bold>
              Valda kolumner
            </EDTypography>
            <span>
              <EDIconButton
                tooltip="Lägg till enskild kolumn"
                onClick={() => props.selectedColumnClick()}
                icon={<EDIcon iconType="add" color="primary" />}
                className={classes.contentButton}
                disabled={props.selectionDisabled}
                disabledReason={props.selectionDisabledReason}
                padding="normal"
              />
              <EDIconButton
                tooltip="Rensa alla"
                onClick={clearAllSelectedColumns}
                icon={<EDIcon iconType="clear_all" color="primary" />}
                className={classes.contentButton}
                disabled={props.selectedColumns.length === 0}
                padding="normal"
              />
            </span>
          </div>
          <div className={classes.chipListContainer}>
            <DraggableChipList
              chipSelection={false}
              clickDisabled
              showHidden
              data-testid="selectedColumns"
              items={props.selectedColumns.map(
                (x): IItem => ({
                  name: x.displayLabel || '',
                  id: x.id,
                  alternateAvatar: x.data.VAND_TECKEN ? 'reverse' : undefined,
                  avatarTooltip: x.data.VAND_TECKEN
                    ? TECKENVAND_KOLUMN
                    : undefined,
                  hide:
                    x.data.HIDE_COLUMN == null ? undefined : x.data.HIDE_COLUMN,
                  labelIcon: (
                    <EDCheckbox
                      checked={!x.data.HIDE_COLUMN}
                      onCheck={createOnHiddenClick(x)}
                      backgroundColorWhite
                      checkboxSpacing={{ paddingLeft: 0 }}
                    />
                  ),
                  deleteIcon: (
                    <FunctionMenu
                      id={'column-select-functionmenu-' + x.id}
                      margin="0px 5px 0px -6px"
                    >
                      <FunctionItem
                        iconType="edit"
                        onClick={createOnEditClick(x)}
                      >
                        Redigera kolumn
                      </FunctionItem>
                      <FunctionItem
                        iconType="delete"
                        onClick={createOnDeleteClick(x)}
                      >
                        Ta bort
                      </FunctionItem>
                    </FunctionMenu>
                  ),
                })
              )}
              onItemsUpdate={props.selectedColumnsUpdate}
              canDelete
              dragHandle
            />
          </div>
        </div>
        <div
          className={classNames(
            classes.contentContainer,
            classes.rightContainer
          )}
        >
          <div className={props.classes.columnFlexContainer}>
            <EDTypography variant="h6" color="textPrimary" bold>
              Kolumnmallar
            </EDTypography>

            <div className={props.classes.templateContainer}>
              <EDTooltip
                title={
                  !props.hasCodePrivilege ? 'Ingen kodbehörighet vald' : ''
                }
                enableClickOnTouch={!props.hasCodePrivilege}
              >
                <span className={props.classes.templSelectContainer}>
                  <EDAutoCompleteEdit
                    label="Välj kolumnmall"
                    notSelectedLabel="Välj kolumnmall"
                    value={kolumnMallarOptions.find(
                      mall => mall.value === props.selectedTemplate
                    )}
                    options={kolumnMallarOptions}
                    onChange={props.changeTemplate}
                    fullWidth
                    disabled={!props.hasCodePrivilege}
                    getOptionLabel={o => o.header}
                  />
                </span>
              </EDTooltip>
              <EkonomiskAnalysColumnSelectTemplateActions
                onClick={props.onSelectTemplateActionsClick}
                open={props.templateActionsOpen}
                flipColumnSign={props.flipColumnSign}
                onFlipSignSwitchChange={onFlipSignSwitchChange}
                prevYearCols={props.prevYearCols}
                onPrevYearColsSwitchChange={onPrevYearColsSwitchChange}
                availableColumnTemplates={props.availableColumnTemplates}
                selectedTemplate={props.selectedTemplate}
              />
            </div>
          </div>
          <EDButton
            label="Lägg till alla kolumner"
            onClick={props.addTemplateColumns}
            disabled={props.selectedTemplate === ''}
            icon={
              <EDIcon marginRight={1} iconType="chevron_left" color="inherit" />
            }
            labelNotUpperCase
            flat
            primary
          />
          <div className={classes.chipListContainer}>
            <DraggableChipList
              chipSelection={false}
              clickDisabled
              showHidden
              data-testid="selectedTemplateColumns"
              items={props.selectedTemplateColumns.map(
                (x, i): IItem => ({
                  name: x.displayLabel || '',
                  id: x.id,
                  alternateAvatar: x.data.VAND_TECKEN ? 'reverse' : undefined,
                  avatarTooltip: x.data.VAND_TECKEN
                    ? TECKENVAND_KOLUMN
                    : undefined,
                  hide:
                    x.data.HIDE_COLUMN == null ? undefined : x.data.HIDE_COLUMN,
                  hoverAvatar: (
                    <EDAvatar
                      color="primary"
                      onClick={() => props.addTemplateColumn(i)}
                      className={classNames(props.classes.moveColumnAvatar)}
                    >
                      <EDTooltip title="Lägg till enskild kolumn">
                        <div className={props.classes.moveColumnAvatarChildren}>
                          <EDIcon iconType={'chevron_left'} />
                        </div>
                      </EDTooltip>
                    </EDAvatar>
                  ),
                })
              )}
              onItemsUpdate={props.selectedTemplateColumnsUpdate}
              dragHandle
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withStyles(EkonomiskAnalysColumnSelectContent, styles);
